import { useState, useEffect } from 'react';
import api from '../../../api/client';

export default function ScenarioComparePage() {
  const [models,   setModels]   = useState([]);
  const [versions, setVersions] = useState([]);
  const [modelId,  setModelId]  = useState('');
  const [baseId,   setBaseId]   = useState('');
  const [compareId,setCompareId]= useState('');
  const [baseLines, setBaseLines] = useState([]);
  const [cmpLines,  setCmpLines]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    api.get('/planning/models').then(({ data }) => setModels(data.data?.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!modelId) { setVersions([]); return; }
    api.get(`/planning/versions?model_id=${modelId}`)
      .then(({ data }) => setVersions(data.data?.items || []))
      .catch(() => {});
  }, [modelId]);

  async function handleCompare() {
    if (!baseId || !compareId) return;
    setLoading(true);
    setError(null);
    try {
      const [b, c] = await Promise.all([
        api.get(`/planning/lines?version_id=${baseId}`),
        api.get(`/planning/lines?version_id=${compareId}`),
      ]);
      setBaseLines(b.data.data?.items || []);
      setCmpLines(c.data.data?.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // Build comparison rows
  const periods   = [...new Set([...baseLines, ...cmpLines].map((l) => l.period))].sort();
  const lineNames = [...new Set([...baseLines, ...cmpLines].map((l) => l.name))];
  const baseMap   = new Map(baseLines.map((l) => [`${l.name}::${l.period}`, l.amount_cents]));
  const cmpMap    = new Map(cmpLines.map((l) => [`${l.name}::${l.period}`, l.amount_cents]));

  return (
    <div data-testid="planning-scenarios-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Scenario Compare</h2>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select
          data-testid="planning-scenario-model-select"
          value={modelId}
          onChange={(e) => { setModelId(e.target.value); setBaseId(''); setCompareId(''); }}
        >
          <option value="">— select model —</option>
          {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>

        <select
          data-testid="planning-scenario-base-select"
          value={baseId}
          onChange={(e) => setBaseId(e.target.value)}
          disabled={!modelId}
        >
          <option value="">— baseline version —</option>
          {versions.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.scenario})</option>)}
        </select>

        <select
          data-testid="planning-scenario-compare-select"
          value={compareId}
          onChange={(e) => setCompareId(e.target.value)}
          disabled={!modelId}
        >
          <option value="">— compare version —</option>
          {versions.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.scenario})</option>)}
        </select>

        <button
          data-testid="planning-scenario-compare"
          onClick={handleCompare}
          disabled={!baseId || !compareId || loading}
        >
          Compare
        </button>
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {(baseLines.length > 0 || cmpLines.length > 0) && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th>Line</th>
                <th>Period</th>
                <th>Baseline</th>
                <th>Compare</th>
                <th>Delta</th>
                <th>Δ%</th>
              </tr>
            </thead>
            <tbody>
              {lineNames.flatMap((name) =>
                periods.map((period) => {
                  const base = baseMap.get(`${name}::${period}`) ?? 0;
                  const cmp  = cmpMap.get(`${name}::${period}`) ?? 0;
                  const delta = cmp - base;
                  const pct   = base !== 0 ? ((delta / Math.abs(base)) * 100).toFixed(1) : '—';
                  return (
                    <tr key={`${name}::${period}`}>
                      <td>{name}</td>
                      <td>{period}</td>
                      <td style={{ textAlign: 'right' }}>{base.toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }}>{cmp.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', color: delta < 0 ? 'red' : delta > 0 ? 'green' : '' }}>
                        {delta >= 0 ? '+' : ''}{delta.toLocaleString()}
                      </td>
                      <td style={{ textAlign: 'right' }}>{typeof pct === 'string' ? pct : `${pct}%`}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {!baseLines.length && !cmpLines.length && !loading && baseId && compareId && (
        <p>No lines to compare.</p>
      )}
    </div>
  );
}
